import { UserDomainService } from './domain';

import type { CognitoIApi, SignUpInput } from '@/infra/cognitoIApi';
import type { Logger } from '@/infra/logger';
import type { WagakoAppIApi } from '@/infra/wagakoAppIApi';
import type { WagakoNurseryIApi } from '@/infra/wagakoNurseryIApi';

/**
 * ユーザーアプリケーションサービス
 */
export class UserApplicationService {
  private service!: UserDomainService;

  /**
   * Injection
   * @param cognitoApi cognitoApi
   * @param wagakoAppApi wagakoAppApi
   * @param wagakoNurseryApi wagakoNurseryApi
   * @param logger logger
   */
  public inject(
    cognitoApi: CognitoIApi,
    wagakoAppApi: WagakoAppIApi,
    wagakoNurseryApi: WagakoNurseryIApi,
    logger: Logger,
  ) {
    this.service = new UserDomainService(logger);
    this.service.inject(
      cognitoApi,
      wagakoAppApi,
      wagakoNurseryApi,
    );
  }

  /**
   * ユーザー情報取得
   * @returns ユーザー情報
   */
  async getUserInfo() {
    const user = await this.service.getUserInfo();
    return user;
  }

  /**
   * ログイン
   * @param username ユーザーID
   * @param password パスワード
   * @returns 結果
   */
  async signIn(
    username: string,
    password: string,
  ) {
    const result = await this.service.signIn(username, password);
    return result;
  }

  /**
   * ログアウト
   */
  async signOut() {
    await this.service.signOut();
  }

  /**
   * 登録
   * @param input 入力
   * @returns 結果
   */
  async signUp(
    input: SignUpInput,
  ) {
    const result = await this.service.signUp(input);
    return result;
  }

  /**
   * 登録の再送信.
   * @param username ユーザー名
   * @returns 結果
   */
  async resendSignUp(
    username: string,
  ) {
    const result = await this.service.resendSignUp(username);
    return result;
  }

  /**
   * 登録を確認
   * @param username ユーザー名
   * @param code コード
   * @returns 結果
   */
  async confirmSignUp(
    username: string,
    code: string,
  ) {
    const result = await this.service.confirmSignUp(username, code);
    return result;
  }

  /**
   * パスワードを忘れた
   * @param username ユーザー名
   * @returns 結果
   */
  async forgotPassword(username: string) {
    const result = await this.service.forgotPassword(username);
    return result;
  }

  /**
   * forgotPasswordSubmit
   * @param username username
   * @param code 認証コード
   * @param password パスワード
   * @returns 結果
   */
  async forgotPasswordSubmit(
    username: string,
    code: string,
    password: string,
  ) {
    const result = await this.service.forgotPasswordSubmit(
      username,
      code,
      password,
    );
    return result;
  }

  /**
   * メールアドレスを変更
   * @param email メールアドレス
   * @returns 結果
   */
  async changeEmail(email: string) {
    const result = await this.service.changeEmail(email);
    return result;
  }

  /**
   * resendChangeEmail
   * @returns 結果
   */
  async resendChangeEmail(): Promise<{
    /** ステータス */
    status: 'ok' | 'error',
    /** メッセージ */
    msg: string,
  }> {
    const result = await this.service.resendChangeEmail();
    return result;
  }

  /**
   * メール検証
   * @param code コード
   * @param email メール
   * @returns 結果
   */
  async verifyEmail(code: string, email: string) {
    const result = await this.service.verifyEmail(code, email);
    return result;
  }

  /**
   * reportUpdateChild
   * @param nurseryId nurseryId
   * @param childId childId
   * @param email email
   */
  async reportUpdateChild(nurseryId: string, childId: string, email: string) {
    const result = await this.service.reportUpdateChild(nurseryId, childId, email);
    return result;
  }
}
